import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import apiClient from '@/lib/api/client';
import { LargeTitleHeader, SegmentedControl, PullToRefresh } from '@/components/common';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PayrollRun {
  id: number;
  run_number: string;
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  pay_frequency: string;
  status: 'draft' | 'approved' | 'paid';
  total_gross: number;
  total_cpp: number;
  total_cpp2: number;
  total_ei: number;
  total_federal_tax: number;
  total_provincial_tax: number;
  total_net: number;
  entry_count: number;
  notes: string | null;
}

interface PayrollEntry {
  id: number;
  employee_id: number;
  employee_name: string;
  sin_last4: string | null;
  regular_hours: number;
  ot_1_5_hours: number;
  ot_2_0_hours: number;
  hourly_rate: number;
  regular_pay: number;
  ot_1_5_pay: number;
  ot_2_0_pay: number;
  gross_pay: number;
  cpp: number;
  cpp2: number;
  ei: number;
  federal_tax: number;
  provincial_tax: number;
  total_deductions: number;
  net_pay: number;
  ytd_gross: number;
  ytd_cpp: number;
  ytd_ei: number;
  ytd_tax: number;
}

interface RunDetail extends PayrollRun {
  entries: PayrollEntry[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  d
    ? new Date(d + 'T00:00:00').toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const fmtHrs = (n: number) =>
  n === 0 ? <span className="text-gray-300">—</span> : <>{n.toFixed(2)}</>;

const statusColors: Record<PayrollRun['status'], string> = {
  draft: 'bg-gray-100 text-gray-600',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Inline editable entry row ────────────────────────────────────────────────

interface EntryRowProps {
  entry: PayrollEntry;
  runStatus: PayrollRun['status'];
  onPaystub: (entry: PayrollEntry) => void;
  onSaved: () => void;
}

const EntryRow = ({ entry, runStatus, onPaystub, onSaved }: EntryRowProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({
    regular_hours: String(entry.regular_hours),
    ot_1_5_hours: String(entry.ot_1_5_hours),
    ot_2_0_hours: String(entry.ot_2_0_hours),
    hourly_rate: String(entry.hourly_rate),
  });

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      await apiClient.patch(`/payroll/entries/${entry.id}`, {
        regular_hours: parseFloat(draft.regular_hours) || 0,
        ot_1_5_hours: parseFloat(draft.ot_1_5_hours) || 0,
        ot_2_0_hours: parseFloat(draft.ot_2_0_hours) || 0,
        hourly_rate: parseFloat(draft.hourly_rate) || 0,
      });
      setEditing(false);
      onSaved();
    } catch {
      setErr('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({
      regular_hours: String(entry.regular_hours),
      ot_1_5_hours: String(entry.ot_1_5_hours),
      ot_2_0_hours: String(entry.ot_2_0_hours),
      hourly_rate: String(entry.hourly_rate),
    });
    setEditing(false);
    setErr('');
  };

  const numInput = (key: keyof typeof draft) => (
    <input
      type="number"
      min="0"
      step="0.01"
      value={draft[key]}
      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      className="w-20 border border-gray-300 rounded px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-[#C77D3F]"
    />
  );

  const canEdit = runStatus === 'draft';

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
          <div>{entry.employee_name}</div>
          {entry.sin_last4 && (
            <div className="text-xs text-gray-400">SIN ···{entry.sin_last4}</div>
          )}
        </td>

        {/* Hours columns */}
        <td className="px-4 py-3 text-right tabular-nums text-sm">
          {editing ? numInput('regular_hours') : fmtHrs(entry.regular_hours)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-sm">
          {editing ? numInput('ot_1_5_hours') : fmtHrs(entry.ot_1_5_hours)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-sm">
          {editing ? numInput('ot_2_0_hours') : fmtHrs(entry.ot_2_0_hours)}
        </td>

        {/* Rate */}
        <td className="px-4 py-3 text-right tabular-nums text-sm text-gray-600">
          {editing ? numInput('hourly_rate') : money(entry.hourly_rate)}
        </td>

        {/* Pay totals */}
        <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold text-gray-900">
          {money(entry.gross_pay)}
        </td>

        {/* Deductions */}
        <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-500">
          {money(entry.cpp)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-500">
          {money(entry.ei)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-500">
          {money(entry.federal_tax)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-500">
          {money(entry.provincial_tax)}
        </td>

        {/* Net pay */}
        <td className="px-4 py-3 text-right tabular-nums text-sm font-bold text-gray-900">
          {money(entry.net_pay)}
        </td>

        {/* Actions */}
        <td className="px-4 py-3 whitespace-nowrap">
          {err && <span className="text-xs text-red-600 mr-1">{err}</span>}
          <div className="flex items-center gap-1.5 justify-end">
            {editing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-xs font-semibold text-white bg-[#C77D3F] hover:bg-[#b36e35] rounded-lg px-3 py-1.5 disabled:opacity-50"
                >
                  {saving ? '…' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="text-xs font-semibold text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button
                    onClick={() => setEditing(true)}
                    className="text-xs font-semibold text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => onPaystub(entry)}
                  className="text-xs font-semibold text-[#C77D3F] border border-[#C77D3F]/30 bg-[#C77D3F]/5 hover:bg-[#C77D3F]/10 rounded-lg px-3 py-1.5"
                >
                  Paystub
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

// ─── Paystub Modal ────────────────────────────────────────────────────────────

interface PaystubModalProps {
  run: RunDetail;
  entry: PayrollEntry;
  onClose: () => void;
}

const PaystubModal = ({ run, entry, onClose }: PaystubModalProps) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 print:hidden">
          <h2 className="text-base font-bold text-gray-900">Pay Statement</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#C77D3F] hover:bg-[#b36e35] rounded-lg px-4 py-2"
            >
              <span className="material-symbols-outlined text-base">print</span>
              Print
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div className="px-8 py-6 space-y-6 print:px-4 print:py-2">
          {/* Company header */}
          <div className="border-b border-gray-200 pb-4">
            <h1 className="text-xl font-bold text-[#C77D3F]">OchrePoint Contracting Ltd.</h1>
            <p className="text-xs text-gray-500 mt-0.5">Duncan & North Cowichan, BC</p>
            <p className="text-sm font-semibold text-gray-700 mt-2">Employee Pay Statement</p>
          </div>

          {/* Employee & period info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <div className="flex gap-2">
                <span className="text-gray-500 w-28">Employee:</span>
                <span className="font-semibold text-gray-900">{entry.employee_name}</span>
              </div>
              {entry.sin_last4 && (
                <div className="flex gap-2">
                  <span className="text-gray-500 w-28">SIN:</span>
                  <span className="font-medium text-gray-700">XXX-XXX-{entry.sin_last4}</span>
                </div>
              )}
              <div className="flex gap-2">
                <span className="text-gray-500 w-28">Hourly Rate:</span>
                <span className="font-medium text-gray-700">{money(entry.hourly_rate)}/hr</span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex gap-2">
                <span className="text-gray-500 w-28">Pay Run:</span>
                <span className="font-semibold text-gray-900">{run.run_number}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-gray-500 w-28">Period:</span>
                <span className="font-medium text-gray-700">
                  {fmtDate(run.pay_period_start)} – {fmtDate(run.pay_period_end)}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-gray-500 w-28">Pay Date:</span>
                <span className="font-medium text-gray-700">{fmtDate(run.pay_date)}</span>
              </div>
            </div>
          </div>

          {/* Earnings table */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Earnings</h3>
            <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-2 font-semibold">Type</th>
                  <th className="text-right px-4 py-2 font-semibold">Hours</th>
                  <th className="text-right px-4 py-2 font-semibold">Rate</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entry.regular_hours > 0 && (
                  <tr>
                    <td className="px-4 py-2.5 text-gray-700">Regular Pay</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{entry.regular_hours.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{money(entry.hourly_rate)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(entry.regular_pay)}</td>
                  </tr>
                )}
                {entry.ot_1_5_hours > 0 && (
                  <tr>
                    <td className="px-4 py-2.5 text-gray-700">Overtime (1.5×)</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{entry.ot_1_5_hours.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                      {money(entry.hourly_rate * 1.5)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(entry.ot_1_5_pay)}</td>
                  </tr>
                )}
                {entry.ot_2_0_hours > 0 && (
                  <tr>
                    <td className="px-4 py-2.5 text-gray-700">Overtime (2.0×)</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{entry.ot_2_0_hours.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                      {money(entry.hourly_rate * 2.0)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(entry.ot_2_0_pay)}</td>
                  </tr>
                )}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-2.5 text-gray-900" colSpan={3}>Gross Pay</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{money(entry.gross_pay)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Deductions table */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Deductions</h3>
            <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-2 font-semibold">Description</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                <tr>
                  <td className="px-4 py-2.5 text-gray-700">CPP Contributions</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{money(entry.cpp)}</td>
                </tr>
                {entry.cpp2 > 0 && (
                  <tr>
                    <td className="px-4 py-2.5 text-gray-700">CPP2 Contributions</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{money(entry.cpp2)}</td>
                  </tr>
                )}
                <tr>
                  <td className="px-4 py-2.5 text-gray-700">EI Premiums</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{money(entry.ei)}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5 text-gray-700">Federal Income Tax</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{money(entry.federal_tax)}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5 text-gray-700">BC Provincial Tax</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{money(entry.provincial_tax)}</td>
                </tr>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-2.5 text-gray-900">Total Deductions</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                    {money(entry.total_deductions)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Net pay callout */}
          <div className="flex items-center justify-between rounded-xl bg-[#C77D3F]/10 border border-[#C77D3F]/20 px-6 py-4">
            <span className="text-base font-bold text-gray-800">Net Pay</span>
            <span className="text-2xl font-extrabold text-[#C77D3F]">{money(entry.net_pay)}</span>
          </div>

          {/* YTD summary */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Year-to-Date Summary
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'YTD Gross', value: entry.ytd_gross },
                { label: 'YTD CPP', value: entry.ytd_cpp },
                { label: 'YTD EI', value: entry.ytd_ei },
                { label: 'YTD Tax', value: entry.ytd_tax },
              ].map(({ label, value }) => (
                <div key={label} className="border border-gray-100 rounded-lg px-3 py-2.5 text-center">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="text-sm font-semibold text-gray-800 mt-0.5">{money(value)}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center border-t border-gray-100 pt-4">
            This is a computer-generated pay statement. OchrePoint Contracting Ltd. — {fmtDate(run.pay_date)}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Create Run Modal ─────────────────────────────────────────────────────────

interface CreateRunModalProps {
  onClose: () => void;
  onCreated: (run: RunDetail) => void;
}

const CreateRunModal = ({ onClose, onCreated }: CreateRunModalProps) => {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    pay_period_start: '',
    pay_period_end: '',
    pay_date: '',
    pay_frequency: 'biweekly',
    notes: '',
  });

  const set = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.pay_period_start || !form.pay_period_end || !form.pay_date) {
      setErr('Please fill in all required date fields.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await apiClient.post('/payroll', {
        pay_period_start: form.pay_period_start,
        pay_period_end: form.pay_period_end,
        pay_date: form.pay_date,
        pay_frequency: form.pay_frequency,
        notes: form.notes || null,
      });
      const created: RunDetail = res.data?.data ?? res.data;
      onCreated(created);
    } catch (ex: unknown) {
      const msg =
        (ex as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErr(msg ?? 'Failed to create payroll run.');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const inputCls =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40 focus:border-[#C77D3F]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">New Payroll Run</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Period Start <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.pay_period_start}
                onChange={set('pay_period_start')}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>
                Period End <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.pay_period_end}
                onChange={set('pay_period_end')}
                className={inputCls}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>
              Pay Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.pay_date}
              onChange={set('pay_date')}
              className={inputCls}
              required
            />
          </div>

          <div>
            <label className={labelCls}>Pay Frequency</label>
            <select value={form.pay_frequency} onChange={set('pay_frequency')} className={inputCls}>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly</option>
              <option value="semimonthly">Semi-Monthly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={set('notes')}
              rows={3}
              placeholder="Any notes for this payroll run…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 flex gap-2 items-start">
            <span className="material-symbols-outlined text-blue-400 text-base mt-0.5">info</span>
            <p className="text-xs text-blue-700">
              Hours are pulled automatically from approved timesheets for the selected period.
            </p>
          </div>

          {err && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-gray-600 border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="text-sm font-semibold text-white bg-[#C77D3F] hover:bg-[#b36e35] rounded-lg px-5 py-2 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create Run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Run Detail View ──────────────────────────────────────────────────────────

interface RunDetailViewProps {
  runId: number;
  onBack: () => void;
}

const RunDetailView = ({ runId, onBack }: RunDetailViewProps) => {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [paystubEntry, setPaystubEntry] = useState<PayrollEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await apiClient.get(`/payroll/${runId}`);
      setRun(res.data?.data ?? res.data);
    } catch {
      setErr('Failed to load payroll run.');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (newStatus: 'approved' | 'paid') => {
    if (!run) return;
    setStatusBusy(true);
    setErr('');
    try {
      const res = await apiClient.patch(`/payroll/${run.id}/status`, { status: newStatus });
      setRun((prev) =>
        prev ? { ...prev, ...(res.data?.data ?? res.data), entries: prev.entries } : prev
      );
    } catch {
      setErr(`Failed to update status.`);
    } finally {
      setStatusBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <span className="material-symbols-outlined animate-spin text-4xl text-gray-300 block mb-3">
          progress_activity
        </span>
        <p className="text-sm text-gray-400">Loading run…</p>
      </div>
    );
  }

  if (err || !run) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-600 mb-3">{err || 'Run not found.'}</p>
        <button onClick={load} className="text-sm text-[#C77D3F] hover:underline font-medium">
          Retry
        </button>
      </div>
    );
  }

  const totalDeductions =
    run.total_cpp + run.total_cpp2 + run.total_ei + run.total_federal_tax + run.total_provincial_tax;

  return (
    <>
      {paystubEntry && (
        <PaystubModal run={run} entry={paystubEntry} onClose={() => setPaystubEntry(null)} />
      )}

      <div className="space-y-5">
        {/* Back + header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="mt-0.5 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              All Runs
            </button>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-bold text-gray-900">{run.run_number}</h2>
                <span
                  className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${statusColors[run.status]}`}
                >
                  {run.status}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {fmtDate(run.pay_period_start)} – {fmtDate(run.pay_period_end)}{' '}
                &middot; Pay date: {fmtDate(run.pay_date)}{' '}
                &middot; {capitalize(run.pay_frequency)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {run.status === 'draft' && (
              <button
                onClick={() => handleStatusChange('approved')}
                disabled={statusBusy}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-base">check_circle</span>
                {statusBusy ? 'Approving…' : 'Approve'}
              </button>
            )}
            {run.status === 'approved' && (
              <button
                onClick={() => handleStatusChange('paid')}
                disabled={statusBusy}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#5C7F4F] hover:bg-[#4e6d43] rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-base">payments</span>
                {statusBusy ? 'Marking…' : 'Mark Paid'}
              </button>
            )}
          </div>
        </div>

        {err && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Summary stat boxes */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Gross</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{money(run.total_gross)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Deductions</p>
            <p className="text-2xl font-bold text-[#B8462E] mt-1">{money(totalDeductions)}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              CPP {money(run.total_cpp)} · EI {money(run.total_ei)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Net Pay</p>
            <p className="text-2xl font-bold text-[#5C7F4F] mt-1">{money(run.total_net)}</p>
            <p className="text-xs text-gray-400 mt-0.5">{run.entry_count} employee{run.entry_count !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Notes */}
        {run.notes && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-5 py-3 text-sm text-amber-800">
            <span className="font-semibold">Notes: </span>{run.notes}
          </div>
        )}

        {/* Entries table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Employee Entries</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{run.entries.length} record{run.entries.length !== 1 ? 's' : ''}</span>
              {run.entries.length > 0 && (
                <button
                  onClick={() => {
                    const header = ['Employee', 'Reg Hrs', 'OT 1.5x', 'OT 2x', 'Rate', 'Gross', 'CPP', 'EI', 'Fed Tax', 'Prov Tax', 'Net Pay'];
                    const rows = run.entries.map(e => [
                      `"${e.employee_name}"`,
                      e.regular_hours.toFixed(2),
                      e.ot_1_5_hours.toFixed(2),
                      e.ot_2_0_hours.toFixed(2),
                      e.hourly_rate.toFixed(2),
                      e.gross_pay.toFixed(2),
                      e.cpp.toFixed(2),
                      e.ei.toFixed(2),
                      e.federal_tax.toFixed(2),
                      e.provincial_tax.toFixed(2),
                      e.net_pay.toFixed(2),
                    ].join(','));
                    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                    a.download = `payroll-${run.run_number}.csv`; a.click();
                  }}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary border border-gray-200 rounded-lg px-2 py-1 hover:bg-primary/5 transition-colors"
                  title="Export to CSV"
                >
                  <span className="material-symbols-outlined text-sm">download</span>
                  CSV
                </button>
              )}
            </div>
          </div>

          {run.entries.length === 0 ? (
            <div className="p-12 text-center">
              <span className="material-symbols-outlined text-5xl text-gray-200 block mb-3">
                group
              </span>
              <p className="text-sm text-gray-400">
                No employee entries. Hours will be pulled from approved timesheets.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold">Employee</th>
                    <th className="text-right px-4 py-3 font-semibold">Reg Hrs</th>
                    <th className="text-right px-4 py-3 font-semibold">OT 1.5×</th>
                    <th className="text-right px-4 py-3 font-semibold">OT 2×</th>
                    <th className="text-right px-4 py-3 font-semibold">Rate</th>
                    <th className="text-right px-4 py-3 font-semibold">Gross</th>
                    <th className="text-right px-4 py-3 font-semibold">CPP</th>
                    <th className="text-right px-4 py-3 font-semibold">EI</th>
                    <th className="text-right px-4 py-3 font-semibold">Fed Tax</th>
                    <th className="text-right px-4 py-3 font-semibold">Prov Tax</th>
                    <th className="text-right px-4 py-3 font-semibold">Net Pay</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {run.entries.map((entry) => (
                    <EntryRow
                      key={entry.id}
                      entry={entry}
                      runStatus={run.status}
                      onPaystub={setPaystubEntry}
                      onSaved={load}
                    />
                  ))}
                </tbody>

                {/* Totals footer */}
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-sm">
                    <td className="px-4 py-3 text-gray-700">Totals</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {run.entries.reduce((s, e) => s + e.regular_hours, 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {run.entries.reduce((s, e) => s + e.ot_1_5_hours, 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {run.entries.reduce((s, e) => s + e.ot_2_0_hours, 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                      {money(run.total_gross)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                      {money(run.total_cpp)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                      {money(run.total_ei)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                      {money(run.total_federal_tax)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                      {money(run.total_provincial_tax)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#5C7F4F]">
                      {money(run.total_net)}
                    </td>
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

type RunFilter = 'all' | 'draft' | 'approved' | 'paid';

const RUN_FILTER_OPTIONS: { value: RunFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export const PayrollPage = () => {
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [runFilter, setRunFilter] = useState<RunFilter>('all');

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await apiClient.get('/payroll');
      setRuns(res.data?.data ?? res.data);
    } catch {
      setErr('Failed to load payroll runs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleCreated = async (created: RunDetail) => {
    setShowCreate(false);
    await loadRuns();
    setSelectedRunId(created.id);
  };

  // ── Run detail view ────────────────────────────────────────────────────────
  if (selectedRunId !== null) {
    return (
      <MainLayout>
        <div>
          <RunDetailView
            runId={selectedRunId}
            onBack={() => setSelectedRunId(null)}
          />
        </div>
      </MainLayout>
    );
  }

  const filteredRuns = runFilter === 'all' ? runs : runs.filter(r => r.status === runFilter);

  // ── Runs list ─────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      {showCreate && (
        <CreateRunModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}

      <div className="space-y-4">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Payroll"
          subtitle="Finance"
          actions={
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 h-9 px-3 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              <span className="hidden sm:inline">New Run</span>
            </button>
          }
        >
          <SegmentedControl<RunFilter>
            options={RUN_FILTER_OPTIONS}
            value={runFilter}
            onChange={setRunFilter}
            size="sm"
          />
        </LargeTitleHeader>

        {err && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{err}</p>
            <button onClick={loadRuns} className="text-sm font-bold text-red-700 hover:underline">
              Retry
            </button>
          </div>
        )}

        {/* Runs Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <span className="material-symbols-outlined animate-spin text-4xl text-gray-300 block mb-3">
                progress_activity
              </span>
              <p className="text-sm text-gray-400">Loading payroll runs…</p>
            </div>
          ) : filteredRuns.length === 0 ? (
            <div className="p-14 text-center">
              <span className="material-symbols-outlined text-6xl text-gray-200 block mb-3">
                payments
              </span>
              <p className="text-sm text-gray-500 mb-1">
                {runFilter === 'all' ? 'No payroll runs yet.' : `No ${runFilter} runs.`}
              </p>
              {runFilter === 'all' && (
                <p className="text-xs text-gray-400">Click "New Run" to create your first payroll run.</p>
              )}
            </div>
          ) : (
            <PullToRefresh onRefresh={loadRuns}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide bg-gray-50">
                      <th className="text-left px-5 py-3 font-semibold">Run #</th>
                      <th className="text-left px-5 py-3 font-semibold">Pay Period</th>
                      <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Pay Date</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Frequency</th>
                      <th className="text-left px-5 py-3 font-semibold">Status</th>
                      <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Employees</th>
                      <th className="text-right px-5 py-3 font-semibold">Gross</th>
                      <th className="text-right px-5 py-3 font-semibold hidden lg:table-cell">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredRuns.map((run) => (
                      <tr
                        key={run.id}
                        onClick={() => setSelectedRunId(run.id)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3.5 font-semibold text-[#C77D3F]">
                          {run.run_number}
                        </td>
                        <td className="px-5 py-3.5 text-gray-700 whitespace-nowrap">
                          {fmtDate(run.pay_period_start)}{' '}
                          <span className="text-gray-400 mx-1">→</span>{' '}
                          {fmtDate(run.pay_period_end)}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell whitespace-nowrap">
                          {fmtDate(run.pay_date)}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell capitalize">
                          {run.pay_frequency}
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${statusColors[run.status]}`}
                          >
                            {run.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right text-gray-500 hidden sm:table-cell">
                          {run.entry_count}
                        </td>
                        <td className="px-5 py-3.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {money(run.total_gross)}
                        </td>
                        <td className="px-5 py-3.5 text-right font-semibold text-[#5C7F4F] whitespace-nowrap hidden lg:table-cell">
                          {money(run.total_net)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PullToRefresh>
          )}
        </div>
      </div>
    </MainLayout>
  );
};
